import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { PropertyWindowContainer } from "./propertywindow-components";
import SettingsWindow from "./settingswindow";
import ColorPropertyWindow from "./color/propertywindow-colorproperty";
import content from "../../../constants/content";
import SubtitlePropertyWindow from "./subtitle/subtitle-propertywindow";
import AnimationPanel from "./animation/animation-panel";
import GradientColorProperty from "./color/propertywindow-gradient";
import { updateTimelineTime } from "../../../redux/actions/timelineUtils";
import useNotify from "../../../helper/hooks/useNotify";
import { pollCleanAudioStatus, setCleaningAudio } from "../../../redux/actions/appUtils";
import useApi from "../../../helper/hooks/useApi";
import { API, API_URL } from "../../../constants/config";
import { setJobtInfo } from "../../../redux/actions/projectUtils";
import { PLAN_LIMITS } from "../../../constants";
import SmartFocusIndex from "./smartfocus/smartfocus-index";

const { AUDIO_CLEANED_SUCCESSFULLY, AUDIO_CLEANING_TRUNED_OFF, FAILED_TO_CLEAN_AUDIO } = content;

const PropertyWindow = (props) => {
    const dispatch = useDispatch();
    const { post } = useApi();
    const { success, warn } = useNotify();
    const appReducer = useSelector(state => state.app);
    const propertyWindow = appReducer.get("propertyWindow");
    const cleanAudioProgress = appReducer.getIn(["cleanAudio", "progress"]);
    const { projectId, plan } = useSelector((state) => state.userDetails);
    const cleanAudioProgressRef = useRef(cleanAudioProgress);
    const components = [];

    useEffect(() => {
        // To Keep refs in sync with redux state to use inside async functions without causing re-renders
        cleanAudioProgressRef.current = cleanAudioProgress;
    }, [cleanAudioProgress]);

    const updateJobInfo = async (taskId, jobData, isDelete = false) => {
        const payload = {
            projectId,
            jobId: taskId,
            name: "bgNoise",
            isDelete,
            ...(!isDelete && { jobData })
        }
        return dispatch(setJobtInfo(payload))
    };

    const onCleanAudioComplete = async (src, sourceType, isNoiseRemoved, itemId, selectionContainer) => {
        const toUpdate = [{
            container: selectionContainer,
            id: itemId,
            toUpdate: {
                src,
                sourceType,
                isNoiseRemoved
            },
        }];
        dispatch(updateTimelineTime({ toUpdate }));
        dispatch(setCleaningAudio({ id: "", isAudioCleaning: false, progress: 0 }));
        success(isNoiseRemoved ? AUDIO_CLEANED_SUCCESSFULLY : AUDIO_CLEANING_TRUNED_OFF);
    };

    const onCleanAudioFailed = async () => {
        dispatch(setCleaningAudio({ id: "", isAudioCleaning: false, progress: 0 }));
        warn(FAILED_TO_CLEAN_AUDIO);
    };

    const onCleanAudio = async (isCleanAudio, item, selectionContainer) => {
        if (cleanAudioProgress) {
            warn("Processing another file. Please hold on...");
            return;
        }
        const startDuration = selectionContainer === "audios" ? "musicStart" : "videoStart";
        const endDuration = selectionContainer === "audios" ? "musicEnd" : "videoEnd";
        const totalDuration = selectionContainer === "audios" ? "musicDuration" : "videoDuration";
        const duration = item.get(endDuration) - item.get(startDuration);
        // Audio or Video below 5 sec is not allowed for BG noise removal.
        if (duration < 5 && isCleanAudio) {
            warn(`File too short to process. Try longer ${selectionContainer === "audios" ? "audio" : "video"}`);
            return;
        }

        if (isCleanAudio) {
            // Check duration limits based on user plan
            const planLimit = PLAN_LIMITS.BG_NOISE[plan];
            if (planLimit !== undefined && duration > planLimit) {
                const medium = selectionContainer === 'audios' ? 'audio' : 'video';
                const mins = Math.floor(planLimit / 60);
                warn(`${plan} plan users can only process ${medium} up to ${mins} ${mins > 1 ? 'minutes' : 'minute'}`);
                return;
            }
        }

        dispatch(setCleaningAudio({ id: item.get("id"), isAudioCleaning: isCleanAudio, progress: 1 }));
        const trim = Math.floor(duration) < Math.floor(item.get(totalDuration));

        const payload = {
            assetId: item.get("assetId"),
            isRemoval: isCleanAudio,
            trim,
            startTime: item.get(startDuration),
            endTime: item.get(endDuration)
        };

        try {
            // Clean audio API call
            const response = await post(`${API_URL}${API.NOISE_REMOVAL.POST}`, payload);
            if (response.success) {
                const { taskId, src, sourceType, outputFile, assetId } = response.data;
                dispatch(setCleaningAudio({ progress: 2 }));

                if (src) {
                    // If noise has already been removed or noise removal is being turned off, it will enter this case.
                    onCleanAudioComplete(src, sourceType, isCleanAudio, item.get("id"), selectionContainer);
                    return;
                }

                try {
                    const jobData = {
                        itemId: item.get("id"),
                        assetId: assetId || item.get("assetId"),
                        outputFile,
                        selectionContainer,
                        isCleanAudio,
                        trim,
                        startTime: item.get(startDuration),
                        endTime: item.get(endDuration)
                    }
                    // Updating the Job info into the project info data
                    await updateJobInfo(taskId, jobData);
                } catch (err) {
                    console.error("Job update failed:", err);
                }
                // Start status call polling
                dispatch(pollCleanAudioStatus({
                    taskId,
                    outputFile,
                    sourceType,
                    itemId: item.get("id"),
                    assetId: assetId || item.get("assetId"),
                    trim,
                    startTime: item.get(startDuration),
                    endTime: item.get(endDuration),
                    selectionContainer,
                    isCleanAudio,
                    cleanAudioProgressRef,
                    projectId,
                    plan,
                    success,
                    warn
                }));
            }
        } catch {
            onCleanAudioFailed();
        }
    };

    if (propertyWindow.get("isOpened") && propertyWindow.get("component") === "settings") {
        components.push(
            <SettingsWindow
                key="settings"
                textPropertyShortcut={props.textPropertyShortcut}
                cleanAudioAction={onCleanAudio}
            />
        );
    }

    if (propertyWindow.get("isOpened") && propertyWindow.get("component") === "color") {
        components.push(<ColorPropertyWindow key="color" />);
    }


    if (propertyWindow.get("isOpened") && propertyWindow.get("component") === content.ANIMATE) {
        components.push(<AnimationPanel key="animate" />);
    }

    if (propertyWindow.get("isOpened") && propertyWindow.get("component") === "subtitle_settings") {
        components.push(<SubtitlePropertyWindow key="subtitle" />);
    }

    if (propertyWindow.get("isOpened") && propertyWindow.get("component") === "DIRECTION") {
        components.push(<GradientColorProperty key="direction" />);
    }

    if (propertyWindow.get("isOpened") && propertyWindow.get("component") === content.SMART_FOCUS) {
        components.push(<SmartFocusIndex key="smartFocus" />);
    }

    return (
        <PropertyWindowContainer>
            {components}
        </PropertyWindowContainer>
    );
}

PropertyWindow.propTypes = {
    textPropertyShortcut: PropTypes.string,
};

export default PropertyWindow;
